import { create } from 'zustand';
import { approveToolCall } from '../api/client';

export interface ApprovalCall {
  callId: string;
  name: string;
  input: Record<string, unknown>;
}

interface ApprovalState {
  /** Chat whose loop is paused awaiting approval, or null when nothing pending. */
  chatId: string | null;
  /** Tool calls in the current approval round (the server gates them as a batch). */
  calls: ApprovalCall[];
  /** True while a decision is being POSTed. */
  submitting: boolean;
  /** Called by the transport layer on an `approval_required` event. */
  request: (chatId: string, call: ApprovalCall) => void;
  /** Approve or reject the whole pending round; unblocks the server loop. */
  respond: (approved: boolean) => Promise<void>;
  clear: () => void;
}

/**
 * Human-in-the-loop tool approval (APPROVAL_MODE=selective|always). The SSE /
 * Centrifugo transports push `approval_required` events here; an app-level
 * <ApprovalModal/> renders the pending round and calls respond(). One decision
 * unblocks the whole round server-side, so calls are accumulated per chat.
 */
export const useApprovalStore = create<ApprovalState>((set, get) => ({
  chatId: null,
  calls: [],
  submitting: false,

  request: (chatId, call) =>
    set((s) => {
      // New chat/round → replace; same chat → append (dedup by callId).
      if (s.chatId !== chatId) {
        return { chatId, calls: [call], submitting: false };
      }
      if (s.calls.some((c) => c.callId === call.callId)) return s;
      return { calls: [...s.calls, call] };
    }),

  respond: async (approved) => {
    const { chatId, submitting } = get();
    if (!chatId || submitting) return;
    set({ submitting: true });
    try {
      await approveToolCall(chatId, approved);
    } catch {
      // Best-effort: if the POST is lost the server loop times out (300s) and
      // rejects on its own. Clear the UI either way so it isn't stuck.
    } finally {
      set({ chatId: null, calls: [], submitting: false });
    }
  },

  clear: () => set({ chatId: null, calls: [], submitting: false }),
}));
